import { Checkbox, Input, Scope } from 'informed';
import { useState } from 'react';
import TymberInfoBox from '../TymberInfoBox';
import classNames from 'classnames';
import { ActionIcon, ContentIcon, FavouriteIcon } from '@tymbe/ui-icons';
import { ApplicationData } from '../../../../types/TymbeApi';
import moment from 'moment';
import ThumbsUp from '../ThumbsUp';
import ThumbsDown from '../ThumbsDown';
import { AttendanceResolution, AttendanceResolutionDisplay } from '../../../../utils/enums';
import DropDownMenu from '../ShiftDetail/DropDownMenu';
import { TyInput } from '@tymbe/ui-forms';
import { BAD_JOB_EVALUATION_SCORE, GOOD_JOB_EVALUATION_SCORE } from '../../../../utils/constants';
import { calculateDurationFromWorkTime } from '../../../../utils/time';
import feathersClient from '../../../../apiClient';
import { useMutation } from 'react-query';
import queryClient from '../../../../queryClient';
import BlockTymber from './BlockTymber';
import Modal from 'react-modal';
import { ErrorAlert, SuccessAlert } from '../../../../components/alerts';
import Spinner from '@tymbe/ty-components/indicators/Spinner';
import Protect from '../../../../apiClient/Protect';
import { Roles } from '@tymbe/schema/enums';
import { informedConfig } from '../../../../config/informed';
import Tooltip from 'rc-tooltip';

interface ResolvedAttendanceRowProps {
  application: ApplicationData;
}

const ResolvedAttendanceRow = ({ application }: ResolvedAttendanceRowProps) => {
  const [openMenu, setOpenMenu] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const { mutateAsync: deleteAttendance, isLoading } = useMutation(
    ['deleteAttendance'],
    async (attendanceId: number) => {
      await feathersClient.service('attendance').remove(attendanceId);
    }
  );
  const onDeleteAttendance = async (attendanceId?: number): Promise<void> => {
    if (!attendanceId) return;
    try {
      await deleteAttendance(attendanceId);
      SuccessAlert(`Docházka byla úspěšně smazána`);
    } catch (error) {
      ErrorAlert(`Při pokusu o smazání docházy došlo k chybě`);
    }

    queryClient.invalidateQueries(['shift']);
  };

  const menuOptions = [
    {
      text: <span className="text-red-700">Ukončit spolupráci</span>,
      onClick: () => setModalOpen(true),
    },
  ];

  const isUserAlreadyBlocked =
    application.person?.blockedCompany
      ?.filter((company) => company.block_deleted_at === null)
      .find((company) => company.id === application.shift?.company?.id) !== undefined;

  return (
    <>
      <Scope scope={`${application.id}`}>
        <div
          className={classNames(
            'p-4 bg-green-100 flex flex-col gap-3 border-secondary-transparent-10 border-[1px] md:grid md:grid-cols-[234px_380px_minmax(300px,1fr)] md:gap-0 md:border-0 first:rounded-tr-3xl first:rounded-tl-3xl last:rounded-br-3xl last:rounded-bl-3xl'
          )}
        >
          <div className="flex items-center gap-2 justify-start">
            <Input
              name="resolved_id"
              required={informedConfig.errorMessage.required}
              type="hidden"
              initialValue={application.id}
            />
            <Checkbox className="size-[18px] rounded" name="resolved_attendance" initialValue={true} disabled />
            <TymberInfoBox personId={application.person_id} isCompanyJunior={application.isCompanyJunior} />
          </div>

          {application.attendance?.resolution === AttendanceResolution.OK ? (
            <div className="flex items-center gap-5 md:gap-10 justify-start">
              <div className="flex flex-col md:flex-row md:gap-6 md:w-48">
                <div className="text-body-2-md md:hidden">Odpracováno</div>
                <TyInput
                  min="0"
                  type="number"
                  step={0.01}
                  mask={(value: string) => Number(value).toFixed(2)}
                  lang="en"
                  className="w-full md:w-[90px]"
                  name="resolved_confirmed_time"
                  suffix="h"
                  initialValue={
                    (Number(application.attendance.confirmed_time) +
                      Number(application.attendance.confirmed_credit_time) +
                      Number(application.attendance.confirmed_overtime)) /
                    60
                  }
                  disabled
                />
                <div className="text-body-2-md md:hidden">Přestávka</div>
                <TyInput
                  min="0"
                  type="number"
                  step={0.01}
                  mask={(value: string) => Number(value).toFixed(2)}
                  lang="en"
                  className="w-full md:w-[75px]"
                  name="resolved_break"
                  suffix="h"
                  initialValue={
                    calculateDurationFromWorkTime(application.attendance.confirmed_time * 60000, false, 'hours') -
                    application.attendance.confirmed_time / 60
                  }
                  disabled
                />
              </div>
            </div>
          ) : (
            <div className="flex gap-2 items-center text-red-500 text-caption-xl font-semibold">
              <ContentIcon name="block" className="size-[18px]" />
              {application.attendance && <span>{AttendanceResolutionDisplay[application.attendance?.resolution]}</span>}
            </div>
          )}

          <div className="flex items-center justify-center flex-col md:flex-row md:gap-2 md:justify-self-end md:items-center">
            <div className="flex gap-1 items-center">
              {application.attendance?.job_evaluation === GOOD_JOB_EVALUATION_SCORE && (
                <ThumbsUp checked className="mb-2 md:mb-0" />
              )}
              {application.attendance?.job_evaluation === BAD_JOB_EVALUATION_SCORE && (
                <ThumbsDown checked className="mb-2 md:mb-0" />
              )}
              <div className="md:flex md:gap-1">
                <span>{moment(application.attendance?.created_at).format('DD.MM.YYYY')}</span>
                <span>
                  {application.attendance?.confirmedBy?.first_name} {application.attendance?.confirmedBy?.last_name}
                </span>
              </div>
            </div>
            {isLoading ? (
              <Spinner className="size-6" />
            ) : (
              <Protect
                auth={[Roles.SUPER_ADMIN, Roles.ADMIN, Roles.TYMBE_ADMIN, Roles.TYMBE_COORDINATOR]}
                redirect={false}
              >
                <button
                  className={classNames(
                    'rounded-full border border-red-300 size-6 p-0 flex justify-center',
                    'items-center text-red-500 disabled:text-secondary-400 disabled:border-secondary-200'
                  )}
                  onClick={() => onDeleteAttendance(application.attendance?.id)}
                >
                  <ActionIcon name="delete_outline" className="size-4" />
                </button>

                <button
                  className={classNames(
                    'relative rounded-full border border-red-300 size-6 p-0 flex justify-center',
                    'items-center text-red-500 disabled:text-secondary-400 disabled:border-secondary-200'
                  )}
                  onClick={() => setOpenMenu(!openMenu)}
                  disabled={isUserAlreadyBlocked}
                >
                  <Tooltip
                    placement="left"
                    trigger={['hover']}
                    overlay="Tymber je již zabanován"
                    overlayClassName={classNames({
                      '!hidden': !isUserAlreadyBlocked,
                    })}
                  >
                    <FavouriteIcon name="more_vert" className="size-4 " />
                  </Tooltip>
                  {openMenu && (
                    <DropDownMenu
                      className="-translate-x-[9rem] translate-y-11"
                      options={menuOptions}
                      onBlur={() => setOpenMenu(false)}
                    />
                  )}
                </button>
              </Protect>
            )}
          </div>
        </div>
      </Scope>

      <Modal
        isOpen={modalOpen}
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEsc={false}
        ariaHideApp={false}
        className="fixed top-[25%] right-[25%] w-[50%] max-h-[70%] bg-bg rounded-lg drop-shadow-lg z-50 flex flex-col"
        overlayClassName="bg-overlay fixed inset-0 z-modal"
      >
        <div className="text-secondary-600 text-heading-4-xl p-4 flex align-center border-b border-secondary-100">
          Ukončit spolupráci
          <FavouriteIcon name="close" onClick={() => setModalOpen(false)} className="size-5 cursor-pointer ml-auto" />
        </div>
        <BlockTymber applicationId={application.id} setModalOpen={setModalOpen} />
      </Modal>
    </>
  );
};

export default ResolvedAttendanceRow;
